import React, { useEffect, useRef, useState } from "react";

// Frozzlers – Enhanced with 3D Visuals matching Online Version
// Features: Guest mode integration, AI opponents, move timer, custom player colors, 3D pucks

const W = 820; // canvas width
const H = 820; // canvas height
const ARENA = { cx: W / 2, cy: H / 2, r: 300 };
const RINGS = [
  { r: 60, score: 10, color: "#f87171" },
  { r: 150, score: 5, color: "#60a5fa" },
  { r: 240, score: 3, color: "#34d399" },
  { r: 300, score: 1, color: "#fbbf24" },
];

const PUCK_R = 14;
const ROCK_R_MIN = 12;
const ROCK_R_MAX = 26;
const WALL_RESTITUTION = 0.9;
const PUCK_RESTITUTION = 0.92;
const ROCK_RESTITUTION = 0.85;
const FRICTION = 0.995;
const STOP_SPEED = 0.06;

const CENTER_DOT_R = 4;

// Launch sectors
const N_SECTORS = 8;
const OUTER_R = RINGS[RINGS.length - 1].r;
const FIXED_ZONE_OFFSET = 50;
const FIXED_ZONE_THICKNESS = 50;
const FIXED_SECTOR_WIDTH_FACTOR = 1.8;

const BASE_ANGLES = [Math.PI / 2, -Math.PI / 2, Math.PI, 0];

// Move timer settings
const MOVE_TIME_LIMIT = 15;
const TIMER_INCREMENT = 0.25;
const AI_THINKING_TIME = {
  'Basic': 1000,
  'iThinker': 2000,
  'Angry Teenager': 500,
  'PhD Scientist': 3000,
  'Insane': 800
};

function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function distance(x1, y1, x2, y2) { return Math.hypot(x2 - x1, y2 - y1); }
function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
function normAngle(a) { while (a <= -Math.PI) a += Math.PI * 2; while (a > Math.PI) a -= Math.PI * 2; return a; }

// Color manipulation helpers for 3D effect
function lightenColor(color, percent) {
  const num = parseInt(color.replace("#",""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) + amt;
  const G = (num >> 8 & 0x00FF) + amt;
  const B = (num & 0x0000FF) + amt;
  return "#" + (0x1000000 + (R<255?R<1?0:R:255)*0x10000 + 
               (G<255?G<1?0:G:255)*0x100 + 
               (B<255?B<1?0:B:255)).toString(16).slice(1);
}

function darkenColor(color, percent) {
  const num = parseInt(color.replace("#",""), 16);
  const amt = Math.round(2.55 * percent);
  const R = (num >> 16) - amt;
  const G = (num >> 8 & 0x00FF) - amt;
  const B = (num & 0x0000FF) - amt;
  return "#" + (0x1000000 + (R>0?R:0)*0x10000 + 
               (G>0?G:0)*0x100 + 
               (B>0?B:0)).toString(16).slice(1);
}

// Enhanced 3D puck drawing function
function draw3DPuck(ctx, pk, PUCK_R) {
  ctx.save();
  
  // Drop shadow for depth
  ctx.shadowColor = "rgba(0,0,0,0.5)";
  ctx.shadowBlur = 8;
  ctx.shadowOffsetX = 3;
  ctx.shadowOffsetY = 3;
  
  // Create gradient for 3D effect
  const gradient = ctx.createRadialGradient(
    pk.x - PUCK_R/3, pk.y - PUCK_R/3, 2,
    pk.x, pk.y, PUCK_R
  );
  gradient.addColorStop(0, lightenColor(pk.color, 30));
  gradient.addColorStop(0.7, pk.color);
  gradient.addColorStop(1, darkenColor(pk.color, 20));
  
  // Draw the main puck body
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(pk.x, pk.y, PUCK_R, 0, Math.PI * 2);
  ctx.fill();
  
  // Reset shadow for rim
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // Dark rim for definition
  ctx.strokeStyle = darkenColor(pk.color, 30);
  ctx.lineWidth = 2;
  ctx.stroke();
  
  // Inner highlight ring for more depth
  ctx.beginPath();
  ctx.arc(pk.x, pk.y, PUCK_R - 2, 0, Math.PI * 2);
  ctx.strokeStyle = lightenColor(pk.color, 20);
  ctx.lineWidth = 1;
  ctx.stroke();
  
  // Glossy highlight on top
  ctx.save();
  ctx.beginPath();
  ctx.arc(pk.x, pk.y, PUCK_R, 0, Math.PI * 2);
  ctx.clip();
  
  ctx.globalAlpha = 0.3;
  ctx.beginPath();
  ctx.ellipse(pk.x, pk.y - PUCK_R/2, PUCK_R * 0.7, PUCK_R * 0.4, 0, 0, Math.PI);
  ctx.fillStyle = "white";
  ctx.fill();
  ctx.restore();
  
  // Score display with better contrast
  const currentScoreForThisPuck = computeScore(pk.x, pk.y);
  if (currentScoreForThisPuck > 0) {
    ctx.shadowColor = "#000000";
    ctx.shadowBlur = 4;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;
    
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 14px 'Inter', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      `${currentScoreForThisPuck}`,
      pk.x,
      pk.y + 1
    );
  }
  
  ctx.restore();
}

function randomRocks(seed, count) {
  const rnd = mulberry32(seed);
  const rocks = [];
  for (let i = 0; i < count; i++) {
    const rr = ROCK_R_MIN + rnd() * (ROCK_R_MAX - ROCK_R_MIN);
    const ang = rnd() * Math.PI * 2;
    const rad = (RINGS[RINGS.length - 1].r - 30) * Math.sqrt(rnd());
    const x = ARENA.cx + Math.cos(ang) * rad;
    const y = ARENA.cy + Math.sin(ang) * rad;
    rocks.push({ x, y, r: rr });
  }
  return rocks;
}

function computeScore(x, y) {
  const d = distance(x, y, ARENA.cx, ARENA.cy);
  if (d <= PUCK_R + CENTER_DOT_R) return 15;
  for (let i = 0; i < RINGS.length; i++) if (d <= RINGS[i].r) return RINGS[i].score;
  return 0;
}

// AI Player system (keeping existing logic)
const AIPlayer = {
  calculateOptimalVelocity: (fromPos, targetPos, difficulty, rng) => {
    const dx = targetPos.x - fromPos.x;
    const dy = targetPos.y - fromPos.y;
    const distance = Math.hypot(dx, dy);
    
    let baseVelocity = distance * 0.045;
    baseVelocity = Math.max(baseVelocity, 3.0);
    baseVelocity = Math.min(baseVelocity, 15);
    
    const dirX = dx / distance;
    const dirY = dy / distance;
    
    let accuracyMultiplier, angleError, powerError;
    
    switch (difficulty) {
      case 'Insane':
        accuracyMultiplier = 0.975;
        angleError = (rng() - 0.5) * 0.015;
        powerError = 0.98 + (rng() * 0.04);
        break;
      case 'PhD Scientist':
        accuracyMultiplier = 0.90;
        angleError = (rng() - 0.5) * 0.035;
        powerError = 0.95 + (rng() * 0.10);
        break;
      case 'Angry Teenager':
        accuracyMultiplier = 0.75;
        angleError = (rng() - 0.5) * 0.06;
        powerError = 0.90 + (rng() * 0.20);
        break;
      case 'iThinker':
        accuracyMultiplier = 0.60;
        angleError = (rng() - 0.5) * 0.10;
        powerError = 0.85 + (rng() * 0.30);
        break;
      case 'Basic':
      default:
        accuracyMultiplier = 0.40;
        angleError = (rng() - 0.5) * 0.17;
        powerError = 0.75 + (rng() * 0.50);
        break;
    }
    
    const perfectAngle = Math.atan2(dy, dx);
    const actualAngle = perfectAngle + angleError;
    const finalDirX = Math.cos(actualAngle);
    const finalDirY = Math.sin(actualAngle);
    
    const finalSpeed = baseVelocity * powerError;
    
    return {
      vx: finalDirX * finalSpeed,
      vy: finalDirY * finalSpeed,
      accuracy: accuracyMultiplier
    };
  },

  analyzeGameState: (existingPucks, currentPlayer, difficulty) => {
    const myPucks = existingPucks.filter(p => p.owner === currentPlayer);
    const opponentPucks = existingPucks.filter(p => p.owner !== currentPlayer);
    
    const myScore = myPucks.reduce((sum, p) => sum + computeScore(p.x, p.y), 0);
    const opponentScore = opponentPucks.reduce((sum, p) => sum + computeScore(p.x, p.y), 0);
    
    const opponentThreats = opponentPucks
      .map(p => ({
        puck: p,
        score: computeScore(p.x, p.y),
        distanceFromCenter: distance(p.x, p.y, ARENA.cx, ARENA.cy),
        threat: computeScore(p.x, p.y) / Math.max(1, distance(p.x, p.y, ARENA.cx, ARENA.cy) / 50)
      }))
      .sort((a, b) => b.threat - a.threat);
    
    const centerDistance = 60;
    const centerArea = existingPucks.filter(p => 
      distance(p.x, p.y, ARENA.cx, ARENA.cy) <= centerDistance
    );
    
    const centerBlocked = centerArea.length > 6;
    const centerDominance = centerArea.filter(p => p.owner === currentPlayer).length - 
                           centerArea.filter(p => p.owner !== currentPlayer).length;
    
    return {
      myScore,
      opponentScore,
      scoreDifference: myScore - opponentScore,
      isWinning: myScore > opponentScore,
      opponentThreats,
      centerArea,
      centerBlocked,
      centerDominance,
      gamePhase: existingPucks.length < 6 ? 'early' : existingPucks.length < 12 ? 'mid' : 'late'
    };
  },

  selectOptimalTarget: (gameState, difficulty, rng, fromPos) => {
    const { 
      scoreDifference, 
      isWinning, 
      opponentThreats, 
      centerBlocked,
      gamePhase 
    } = gameState;
    
    let targetPos;
    
    if (difficulty === 'Basic' || rng() < 0.5) {
      targetPos = { x: ARENA.cx + (rng() - 0.5) * 80, y: ARENA.cy + (rng() - 0.5) * 80 };
    } else {
      targetPos = { x: ARENA.cx + (rng() - 0.5) * 35, y: ARENA.cy + (rng() - 0.5) * 35 };
    }
    
    return { targetPos, strategy: 'basic', priority: 'score' };
  },

  getPlacement: (difficulty, sectorGeom, existingPucks, rocks, rng) => {
    const { centerAngle, innerR, outerR } = sectorGeom;
    
    let targetR, targetAngle;
    
    targetR = innerR + (outerR - innerR) * (0.25 + rng() * 0.5);
    targetAngle = centerAngle + (rng() - 0.5) * 0.35;
    
    return {
      x: ARENA.cx + Math.cos(targetAngle) * targetR,
      y: ARENA.cy + Math.sin(targetAngle) * targetR
    };
  },
  
  getAim: (difficulty, fromPos, existingPucks, rocks, rng, currentPlayer) => {
    const gameState = AIPlayer.analyzeGameState(existingPucks, currentPlayer, difficulty);
    const { targetPos } = AIPlayer.selectOptimalTarget(gameState, difficulty, rng, fromPos);
    const velocity = AIPlayer.calculateOptimalVelocity(fromPos, targetPos, difficulty, rng);
    
    return {
      targetX: targetPos.x,
      targetY: targetPos.y,
      velocity: velocity
    };
  }
};

const ModernButton = ({ children, onClick, variant = "default", style = {}, ...props }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);

  const getButtonStyle = () => {
    let baseStyle = {
      padding: '12px 24px',
      borderRadius: '8px',
      border: 'none',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
      ...style
    };

    if (variant === "primary") {
      baseStyle = {
        ...baseStyle,
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        padding: '16px 32px',
        fontSize: '18px',
        fontWeight: 'bold',
        borderRadius: '12px'
      };
    } else if (variant === "danger") {
      baseStyle = {
        ...baseStyle,
        backgroundColor: '#dc2626',
        color: 'white',
        border: '1px solid #b91c1c'
      };
    } else {
      baseStyle = {
        ...baseStyle,
        backgroundColor: '#374151',
        color: 'white',
        border: '1px solid #4b5563'
      };
    }

    if (isPressed) {
      baseStyle.transform = 'scale(0.98)';
    } else if (isHovered) {
      baseStyle.transform = 'scale(1.02)';
      baseStyle.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
    }

    return baseStyle;
  };

  return (
    <button
      style={getButtonStyle()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setIsPressed(false);
      }}
      onMouseDown={() => setIsPressed(true)}
      onMouseUp={() => setIsPressed(false)}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};

// Enhanced background component with proper overlay
const PageBG = ({ src = "/bg/frozzle_me_plenty.jpg", children }) => {
  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      width: '100%',
      backgroundImage: `url(${src})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundAttachment: 'fixed'
    }}>
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)', // 50% black overlay for darkness
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'relative',
        zIndex: 1
      }}>
        {children}
      </div>
    </div>
  );
};

export default function Frozzlers() {
  const canvasRef = useRef(null);

  // Guest mode configuration
  const [gameConfig, setGameConfig] = useState(null);
  const [isGuestMode, setIsGuestMode] = useState(false);
  const [playerConfigs, setPlayerConfigs] = useState([]);

  // Game state
  const [numPlayers, setNumPlayers] = useState(2);
  const [pucksPerPlayer, setPucksPerPlayer] = useState(5);
  const [gameState, setGameState] = useState("lobby");
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1e9));
  const [rocks, setRocks] = useState(() => randomRocks(42, 12));
  const [turn, setTurn] = useState(0);
  const [pucks, setPucks] = useState([]);
  const [aim, setAim] = useState(null);
  const [phase, setPhase] = useState("place");
  const [sectorIdx, setSectorIdx] = useState([0, 0, 0, 0]);
  const [placementPos, setPlacementPos] = useState(null);

  // Move timer state
  const [timeLeft, setTimeLeft] = useState(MOVE_TIME_LIMIT);
  const [timerActive, setTimerActive] = useState(false);

  // AI state
  const [aiThinking, setAiThinking] = useState(false);
  const [aiRng] = useState(() => mulberry32(Date.now()));

  const totalShots = numPlayers * pucksPerPlayer;
  const shotsTaken = turn;
  const currentPlayer = shotsTaken % numPlayers;
  const currentPlayerConfig = playerConfigs[currentPlayer] || { name: `Player ${currentPlayer + 1}`, isAI: false, color: "#ef4444" };

  // Load guest mode configuration on mount
  useEffect(() => {
    const storedConfig = sessionStorage.getItem('gameConfig');
    if (storedConfig) {
      try {
        const config = JSON.parse(storedConfig);
        setGameConfig(config);
        setIsGuestMode(config.isGuestMode || false);
        setPlayerConfigs(config.players || []);
        setNumPlayers(config.players?.length || 2);
        setPucksPerPlayer(config.pucksPerTurn || 5);
        
        if (config.isGuestMode) {
          setGameState("playing");
          setTimeout(() => {
            const firstPlayerIsHuman = !config.players[0]?.isAI;
            setTimerActive(firstPlayerIsHuman);
          }, 100);
        }
      } catch (error) {
        console.error('Failed to parse game config:', error);
      }
    }
  }, []);

  // Sector geometry
  const ZONE_RADIUS = OUTER_R + FIXED_ZONE_OFFSET;
  const ZONE_THICKNESS = FIXED_ZONE_THICKNESS;
  const baseWidth = (OUTER_R / 4) / ZONE_RADIUS;
  const SECTOR_ANGLE_WIDTH = baseWidth * FIXED_SECTOR_WIDTH_FACTOR;

  function currentSectorGeom() {
    const baseAngle = BASE_ANGLES[currentPlayer % BASE_ANGLES.length] || 0;
    const idx = sectorIdx[currentPlayer] % N_SECTORS;
    const centerAngle = baseAngle + (idx * (2 * Math.PI / N_SECTORS));
    const startA = centerAngle - SECTOR_ANGLE_WIDTH / 2;
    const endA = centerAngle + SECTOR_ANGLE_WIDTH / 2;
    const innerR = ZONE_RADIUS - ZONE_THICKNESS / 2;
    const outerR = ZONE_RADIUS + ZONE_THICKNESS / 2;
    return { centerAngle, startA, endA, innerR, outerR };
  }

  function playerColor(idx) {
    if (playerConfigs[idx]?.color) {
      return playerConfigs[idx].color;
    }
    const palette = ["#ef4444", "#3b82f6", "#10b981", "#a855f7"];
    return palette[idx % palette.length];
  }

  function clampToSector(x, y) {
    const { startA, endA, innerR, outerR } = currentSectorGeom();
    let ang = Math.atan2(y - ARENA.cy, x - ARENA.cx);
    let r = distance(ARENA.cx, ARENA.cy, x, y);

    const rMin = innerR + PUCK_R;
    const rMax = outerR - PUCK_R;
    r = clamp(r, rMin, rMax);

    const mid = normAngle((startA + endA) / 2);
    const half = normAngle(endA - mid);
    const angPad = Math.asin(Math.min(1, PUCK_R / Math.max(r, 0.0001)));
    const allowed = Math.max(0, Math.abs(half) - angPad);

    let d = normAngle(ang - mid);
    if (d > allowed) d = allowed;
    if (d < -allowed) d = -allowed;
    ang = mid + d;

    return { x: ARENA.cx + Math.cos(ang) * r, y: ARENA.cy + Math.sin(ang) * r };
  }

  function startGame() {
    setSeed((s) => s);
    setRocks(randomRocks(seed, 12 + (seed % 5)));
    setPucks([]);
    setTurn(0);
    setPhase("place");
    setSectorIdx([0, 0, 0, 0]);
    setPlacementPos(null);
    setGameState("playing");
    setTimeLeft(MOVE_TIME_LIMIT);
    const firstPlayerIsHuman = !playerConfigs[0]?.isAI;
    setTimerActive(firstPlayerIsHuman);
  }

  // Move timer effect
  useEffect(() => {
    if (!timerActive || gameState !== "playing" || phase === "rolling" || phase === "roundEnd") {
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= TIMER_INCREMENT) {
          handleTimeout();
          return MOVE_TIME_LIMIT;
        }
        return prev - TIMER_INCREMENT;
      });
    }, TIMER_INCREMENT * 1000);

    return () => clearInterval(timer);
  }, [timerActive, gameState, phase, currentPlayer]);

  const handleTimeout = () => {
    if (phase === "place") {
      const sectorGeom = currentSectorGeom();
      const randomPos = AIPlayer.getPlacement('Basic', sectorGeom, pucks, rocks, aiRng);
      const clamped = clampToSector(randomPos.x, randomPos.y);
      setPlacementPos(clamped);
      setPhase("aim");
    } else if (phase === "aim") {
      if (placementPos) {
        const randomAngle = aiRng() * Math.PI * 2;
        const randomPower = 50 + aiRng() * 50;
        const vx = Math.cos(randomAngle) * randomPower * 0.06;
        const vy = Math.sin(randomAngle) * randomPower * 0.06;
        
        const newPuck = {
          x: placementPos.x,
          y: placementPos.y,
          vx: -vx,
          vy: -vy,
          r: PUCK_R,
          owner: currentPlayer,
          color: playerColor(currentPlayer)
        };
        
        setPucks(prev => [...prev, newPuck]);
        setAim(null);
        setPlacementPos(null);
        setPhase("rolling");
        setTimerActive(false);
      }
    }
  };

  // AI move execution
  useEffect(() => {
    if (gameState !== "playing" || !currentPlayerConfig.isAI || aiThinking || phase === "rolling" || phase === "roundEnd") {
      return;
    }

    const executeAIMove = () => {
      setAiThinking(true);
      setTimerActive(false);
      
      const thinkingTime = AI_THINKING_TIME[currentPlayerConfig.aiDifficulty] || 1000;
      
      setTimeout(() => {
        if (phase === "place") {
          const sectorGeom = currentSectorGeom();
          const aiPlacement = AIPlayer.getPlacement(
            currentPlayerConfig.aiDifficulty,
            sectorGeom,
            pucks,
            rocks,
            aiRng
          );
          const clamped = clampToSector(aiPlacement.x, aiPlacement.y);
          setPlacementPos(clamped);
          setPhase("aim");
          
          setTimeout(() => {
            const aiAim = AIPlayer.getAim(
              currentPlayerConfig.aiDifficulty,
              clamped,
              pucks,
              rocks,
              aiRng,
              currentPlayer
            );
            
            const vx = aiAim.velocity.vx;
            const vy = aiAim.velocity.vy;
            
            const newPuck = {
              x: clamped.x,
              y: clamped.y,
              vx,
              vy,
              r: PUCK_R,
              owner: currentPlayer,
              color: playerColor(currentPlayer)
            };
            setPucks(prev => [...prev, newPuck]);
            setAim(null);
            setPlacementPos(null);
            setPhase("rolling");
            setAiThinking(false);
          }, 500);
        }
      }, thinkingTime);
    };

    executeAIMove();
  }, [gameState, currentPlayerConfig.isAI, phase, currentPlayer, aiThinking]);

  // Reset timer when turn changes
  useEffect(() => {
    if (gameState === "playing" && phase === "place") {
      setTimeLeft(MOVE_TIME_LIMIT);
      const isHumanPlayer = !currentPlayerConfig.isAI;
      setTimerActive(isHumanPlayer);
    }
  }, [turn, phase, currentPlayerConfig.isAI, gameState]);

  // If numbers change mid-run, reset the round
  useEffect(() => {
    if (gameState !== "playing") return;
    setRocks(randomRocks(seed, 12 + (seed % 5)));
    setPucks([]);
    setTurn(0);
    setPhase("place");
    setSectorIdx([0, 0, 0, 0]);
    setPlacementPos(null);
  }, [seed, numPlayers, pucksPerPlayer, gameState]);

  // Physics + render loop
  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    let raf;

    const loop = () => { 
      stepPhysics(); 
      draw(); 
      raf = requestAnimationFrame(loop); 
    };

    const stepPhysics = () => {
      if (pucks.length === 0 || gameState !== "playing") return;

      for (const pk of pucks) {
        pk.x += pk.vx; 
        pk.y += pk.vy;
        pk.vx *= FRICTION; 
        pk.vy *= FRICTION;
        if (Math.hypot(pk.vx, pk.vy) < STOP_SPEED) { 
          pk.vx = 0; 
          pk.vy = 0; 
        }
        if (pk.x - PUCK_R < 0) { 
          pk.x = PUCK_R; 
          pk.vx = -pk.vx * WALL_RESTITUTION; 
        }
        if (pk.x + PUCK_R > W) { 
          pk.x = W - PUCK_R; 
          pk.vx = -pk.vx * WALL_RESTITUTION; 
        }
        if (pk.y - PUCK_R < 0) { 
          pk.y = PUCK_R; 
          pk.vy = -pk.vy * WALL_RESTITUTION; 
        }
        if (pk.y + PUCK_R > H) { 
          pk.y = H - PUCK_R; 
          pk.vy = -pk.vy * WALL_RESTITUTION; 
        }
      }

      // Puck↔puck collisions
      for (let i = 0; i < pucks.length; i++) {
        for (let j = i + 1; j < pucks.length; j++) {
          const a = pucks[i], b = pucks[j];
          const dx = b.x - a.x, dy = b.y - a.y; 
          const dist = Math.hypot(dx, dy);
          const minDist = PUCK_R * 2;
          if (dist > 0 && dist < minDist) {
            const overlap = (minDist - dist) / 2; 
            const nx = dx / dist, ny = dy / dist;
            a.x -= nx * overlap; 
            a.y -= ny * overlap; 
            b.x += nx * overlap; 
            b.y += ny * overlap;
            const relVx = b.vx - a.vx; 
            const relVy = b.vy - a.vy; 
            const vn = relVx * nx + relVy * ny;
            if (vn < 0) {
              const imp = -(1 + PUCK_RESTITUTION) * vn / 2; 
              const impX = imp * nx, impY = imp * ny;
              a.vx -= impX; 
              a.vy -= impY; 
              b.vx += impX; 
              b.vy += impY;
            }
          }
        }
      }

      // Puck↔rock collisions
      for (const pk of pucks) {
        for (const rk of rocks) {
          const dx = pk.x - rk.x;
          const dy = pk.y - rk.y;
          const dist = Math.hypot(dx, dy);
          const minDist = PUCK_R + rk.r;
          if (dist > 0 && dist < minDist) {
            const overlap = minDist - dist;
            const nx = dx / dist, ny = dy / dist;
            pk.x += nx * overlap;
            pk.y += ny * overlap;
            const vn = pk.vx * nx + pk.vy * ny;
            if (vn < 0) {
              pk.vx -= 2 * vn * nx * ROCK_RESTITUTION;
              pk.vy -= 2 * vn * ny * ROCK_RESTITUTION;
            }
          }
        }
      }

      // Check if all pucks stopped moving
      const allStopped = pucks.every(pk => Math.hypot(pk.vx, pk.vy) < STOP_SPEED);
      if (phase === "rolling" && allStopped) {
        if (pucks.length > 0) {
          const lastPuck = pucks[pucks.length - 1];
          const score = computeScore(lastPuck.x, lastPuck.y);
          lastPuck._scored = score;
        }
        
        setTurn(prev => {
          const newTurn = prev + 1;
          if (newTurn >= totalShots) {
            setPhase("roundEnd");
            setTimerActive(false);
          } else {
            setPhase("place");
            setTimeLeft(MOVE_TIME_LIMIT);
            const nextPlayer = newTurn % numPlayers;
            setSectorIdx(prev => {
              const newIdx = [...prev];
              newIdx[nextPlayer] = (newIdx[nextPlayer] + 1) % N_SECTORS;
              return newIdx;
            });
          }
          return newTurn;
        });
      }
    };

    const draw = () => {
      // Clear canvas completely
      ctx.clearRect(0, 0, W, H);
      
      // Fill entire canvas with semi-transparent black (20% opacity)
      ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
      ctx.fillRect(0, 0, W, H);
      
      // Draw arena background with solid gradient
      const gradient = ctx.createRadialGradient(ARENA.cx, ARENA.cy, 0, ARENA.cx, ARENA.cy, ARENA.r);
      gradient.addColorStop(0, "#1e293b");
      gradient.addColorStop(0.7, "#334155");
      gradient.addColorStop(1, "#475569");
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(ARENA.cx, ARENA.cy, ARENA.r, 0, Math.PI * 2);
      ctx.fill();

      // Simple arena border without excessive glow
      ctx.strokeStyle = "#60a5fa";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ARENA.cx, ARENA.cy, ARENA.r, 0, Math.PI * 2);
      ctx.stroke();

      // Draw scoring rings with clean styling
      for (let i = RINGS.length - 1; i >= 0; i--) {
        const ring = RINGS[i];
        
        // Simple ring outline
        ctx.strokeStyle = ring.color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(ARENA.cx, ARENA.cy, ring.r, 0, Math.PI * 2);
        ctx.stroke();
        
        // Ring labels with simple shadow for readability
        const textX = ARENA.cx + ring.r - 25;
        const textY = ARENA.cy + 5;
        
        ctx.shadowColor = "#000000";
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 18px 'Inter', system-ui, sans-serif";
        ctx.fillText(ring.score.toString(), textX, textY);
        
        ctx.shadowBlur = 0;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
      ctx.globalAlpha = 1;

      // Center dot with glow
      ctx.shadowColor = "#ffffff";
      ctx.shadowBlur = 8;
      ctx.fillStyle = "#ffffff";
      ctx.beginPath();
      ctx.arc(ARENA.cx, ARENA.cy, CENTER_DOT_R, 0, Math.PI * 2);
      ctx.fill();
      ctx.shadowBlur = 0;

      // Visible outer board boundary
      ctx.save();
      ctx.strokeStyle = "#94a3b8";
      ctx.lineWidth = 2;
      ctx.globalAlpha = 0.9;
      ctx.strokeRect(1, 1, W - 2, H - 2);
      ctx.restore();

      // Draw current player's launch sector
      if (gameState === "playing" && phase !== "roundEnd") {
        const { startA, endA, innerR, outerR } = currentSectorGeom();
        
        ctx.shadowColor = playerColor(currentPlayer);
        ctx.shadowBlur = 15;
        ctx.fillStyle = playerColor(currentPlayer) + "30";
        ctx.strokeStyle = playerColor(currentPlayer);
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(ARENA.cx, ARENA.cy, outerR, startA, endA);
        ctx.arc(ARENA.cx, ARENA.cy, innerR, endA, startA, true);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Placement ghost with 3D preview
      if ((phase === "place" || phase === "aim") && placementPos && gameState === "playing") {
        ctx.save();
        ctx.globalAlpha = 0.7;
        
        const ghostPuck = {
          x: placementPos.x,
          y: placementPos.y,
          color: playerColor(currentPlayer)
        };
        
        draw3DPuck(ctx, ghostPuck, PUCK_R);
        
        // Add pulsing glow effect
        const pulseScale = 1 + Math.sin(Date.now() * 0.003) * 0.05;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.arc(placementPos.x, placementPos.y, PUCK_R * pulseScale * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = playerColor(currentPlayer);
        ctx.fill();
        
        ctx.restore();
      }

      // Rocks with enhanced 3D effect
      ctx.save();
      for (const rk of rocks) {
        const g = ctx.createRadialGradient(
          rk.x - rk.r / 3,
          rk.y - rk.r / 3,
          2,
          rk.x,
          rk.y,
          rk.r
        );
        g.addColorStop(0, "#6b7280");
        g.addColorStop(1, "#374151");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(rk.x, rk.y, rk.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = "#1f2937";
        ctx.stroke();
      }
      ctx.restore();

      // Draw existing pucks with 3D rendering
      for (const pk of pucks) {
        draw3DPuck(ctx, pk, PUCK_R);
      }

      // Power bar and aim band (matching online layout)
      const BAR_W = 200, BAR_H = 12;
      const bx = W - 250, by = 60;
      
      ctx.fillStyle = "#111827";
      ctx.fillRect(bx - 2, by - 2, BAR_W + 4, BAR_H + 4);
      ctx.fillStyle = "#e5e7eb";
      ctx.fillRect(bx, by, BAR_W, BAR_H);

      if (phase === "aim" && aim && gameState === "playing" && placementPos) {
        const start = placementPos;
        const curr = aim.curr;
        const dx = curr.x - start.x;
        const dy = curr.y - start.y;
        const ang = Math.atan2(dy, dx);
        const dist = Math.hypot(dx, dy);
        const MAX_DRAG = 200;

        ctx.strokeStyle = playerColor(currentPlayer);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(start.x, start.y);
        ctx.lineTo(curr.x, curr.y);
        ctx.stroke();
        const ah = 10;
        ctx.beginPath();
        ctx.moveTo(curr.x, curr.y);
        ctx.lineTo(curr.x - ah * Math.cos(ang - 0.3), curr.y - ah * Math.sin(ang - 0.3));
        ctx.lineTo(curr.x - ah * Math.cos(ang + 0.3), curr.y - ah * Math.sin(ang + 0.3));
        ctx.closePath();
        ctx.fillStyle = playerColor(currentPlayer);
        ctx.fill();

        const pct = Math.min(1, dist / MAX_DRAG);
        ctx.fillStyle = playerColor(currentPlayer);
        ctx.fillRect(bx, by, BAR_W * pct, BAR_H);
      }

      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 1;
      ctx.strokeRect(bx, by, BAR_W, BAR_H);
      ctx.fillStyle = "#ffffff";
      ctx.font = "bold 11px sans-serif";
      ctx.fillText("POWER", bx - 90, by + BAR_H - 2);

      // Move Timer Display
      if (gameState === "playing" && phase !== "roundEnd" && timerActive) {
        const timerX = bx;
        const timerY = by + 30;
        const timerW = BAR_W;
        const timerH = 8;
        
        ctx.fillStyle = "#374151";
        ctx.fillRect(timerX, timerY, timerW, timerH);
        
        const timePercent = timeLeft / MOVE_TIME_LIMIT;
        const timerColor = timeLeft <= 5 ? "#ef4444" : timeLeft <= 10 ? "#f59e0b" : "#22c55e";
        ctx.fillStyle = timerColor;
        ctx.fillRect(timerX, timerY, timerW * timePercent, timerH);
        
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 1;
        ctx.strokeRect(timerX, timerY, timerW, timerH);
        
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 11px sans-serif";
        ctx.fillText(`TIME: ${timeLeft.toFixed(2)}s`, timerX - 90, timerY + timerH - 1);
      }

      // Enhanced dual player score display (matching online game)
      if (gameState === "playing") {
        const currentScores = new Array(numPlayers).fill(0);
        for (const pk of pucks) {
          currentScores[pk.owner] += computeScore(pk.x, pk.y);
        }
        
        const hudY = 25;
        const playerSpacing = 200;
        
        for (let i = 0; i < numPlayers; i++) {
          const playerConfig = playerConfigs[i] || { name: `Player ${i + 1}`, isAI: false };
          const playerName = playerConfig.name || `Player ${i + 1}`;
          const isCurrentPlayer = i === currentPlayer;
          const score = currentScores[i];
          
          if (isCurrentPlayer) {
            ctx.fillStyle = "rgba(96, 165, 250, 0.2)";
            ctx.fillRect(10 + (i * playerSpacing), hudY - 18, 180, 22);
            ctx.strokeStyle = "#60a5fa";
            ctx.lineWidth = 1;
            ctx.strokeRect(10 + (i * playerSpacing), hudY - 18, 180, 22);
          }
          
          ctx.fillStyle = playerColor(i);
          ctx.fillRect(15 + (i * playerSpacing), hudY - 12, 12, 12);
          
          ctx.fillStyle = isCurrentPlayer ? "#60a5fa" : "#ffffff";
          ctx.font = isCurrentPlayer ? "bold 14px 'Inter', system-ui, sans-serif" : "12px 'Inter', system-ui, sans-serif";
          ctx.fillText(`${playerName}: ${score} pts`, 32 + (i * playerSpacing), hudY - 2);
          
          if (playerConfig.isAI) {
            ctx.fillStyle = "#9ca3af";
            ctx.font = "10px 'Inter', system-ui, sans-serif";
            const aiText = aiThinking && isCurrentPlayer ? "AI Thinking..." : `AI (${playerConfig.aiDifficulty})`;
            ctx.fillText(aiText, 32 + (i * playerSpacing), hudY + 10);
          }
        }
        
        // Turn counter
        ctx.fillStyle = "#9ca3af";
        ctx.font = "11px 'Inter', system-ui, sans-serif";
        ctx.fillText(`Turn ${shotsTaken + 1} of ${totalShots}`, W - 120, timerActive ? 120 : 100);
      } else {
        ctx.fillStyle = "#ffffff";
        ctx.font = "12px ui-sans-serif, system-ui, -apple-system";
        ctx.fillText("Frozzlers – set players & pucks, then Start Game", 12, 18);
      }

      // Round end modal with enhanced navigation (matching online game)
      if (phase === "roundEnd" && gameState === "playing") {
        ctx.fillStyle = "rgba(0,0,0,0.8)";
        ctx.fillRect(0, 0, W, H);
        
        const agg = new Array(numPlayers).fill(0);
        for (const pk of pucks) agg[pk.owner] += computeScore(pk.x, pk.y);
        const winner = [...Array(numPlayers).keys()].reduce((best, i) => (agg[i] > agg[best] ? i : best), 0);
        
        const modalW = 420;
        const modalH = 380;
        const modalX = (W - modalW) / 2;
        const modalY = (H - modalH) / 2;
        
        const modalGradient = ctx.createLinearGradient(modalX, modalY, modalX, modalY + modalH);
        modalGradient.addColorStop(0, "rgba(30, 41, 59, 0.98)");
        modalGradient.addColorStop(1, "rgba(15, 23, 42, 0.98)");
        ctx.fillStyle = modalGradient;
        ctx.fillRect(modalX, modalY, modalW, modalH);
        
        ctx.shadowColor = "#60a5fa";
        ctx.shadowBlur = 20;
        ctx.strokeStyle = "#60a5fa";
        ctx.lineWidth = 2;
        ctx.strokeRect(modalX, modalY, modalW, modalH);
        ctx.shadowBlur = 0;
        
        ctx.strokeStyle = "rgba(96, 165, 250, 0.3)";
        ctx.lineWidth = 1;
        ctx.strokeRect(modalX + 2, modalY + 2, modalW - 4, modalH - 4);
        
        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 32px 'Inter', system-ui, sans-serif";
        ctx.textAlign = "center";
        ctx.fillText("Game Over", modalX + modalW/2, modalY + 50);
        
        let scoreY = modalY + 90;
        ctx.font = "18px 'Inter', system-ui, sans-serif";
        
        const standings = [];
        for (let i = 0; i < numPlayers; i++) {
          standings.push({ index: i, score: agg[i], name: playerConfigs[i]?.name || `Player ${i + 1}` });
        }
        standings.sort((a, b) => b.score - a.score);
        
        for (let rank = 0; rank < standings.length; rank++) {
          const player = standings[rank];
          const isWinner = rank === 0;
          
          ctx.fillStyle = isWinner ? "#fbbf24" : "#9ca3af";
          ctx.textAlign = "left";
          ctx.font = "bold 16px 'Inter', system-ui, sans-serif";
          ctx.fillText(`${rank + 1}.`, modalX + 40, scoreY);
          
          ctx.fillStyle = playerColor(player.index);
          ctx.fillRect(modalX + 70, scoreY - 12, 20, 16);
          
          ctx.fillStyle = isWinner ? "#fbbf24" : "#ffffff";
          ctx.font = isWinner ? "bold 18px 'Inter', system-ui, sans-serif" : "16px 'Inter', system-ui, sans-serif";
          ctx.fillText(`${player.name}: ${player.score} pts`, modalX + 100, scoreY);
          
          scoreY += 30;
        }
        
        const winnerY = scoreY + 10;
        ctx.fillStyle = "#fbbf24";
        ctx.font = "bold 20px 'Inter', system-ui, sans-serif";
        ctx.textAlign = "center";
        const winnerName = playerConfigs[winner]?.name || `Player ${winner + 1}`;
        ctx.fillText(`🏆 ${winnerName} Wins!`, modalX + modalW/2, winnerY);
        
        const statsY = winnerY + 35;
        ctx.fillStyle = "#9ca3af";
        ctx.font = "14px 'Inter', system-ui, sans-serif";
        const gameMode = isGuestMode ? "Guest Mode" : "Local Game";
        const gameStats = `${gameMode} • ${numPlayers} Players • ${pucksPerPlayer} Pucks Each`;
        ctx.fillText(gameStats, modalX + modalW/2, statsY);
        
        ctx.fillStyle = "#64748b";
        ctx.font = "12px 'Inter', system-ui, sans-serif";
        ctx.fillText("Click anywhere to continue", modalX + modalW/2, modalY + modalH - 25);
        
        ctx.textAlign = "start";
      }
    };

    loop();
    return () => cancelAnimationFrame(raf);
  }, [pucks, rocks, phase, turn, totalShots, seed, numPlayers, sectorIdx, shotsTaken, currentPlayer, gameState, placementPos, aim, timeLeft, timerActive, aiThinking, playerConfigs]);

  // Input handling
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const getMousePos = (e) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    const onMove = (e) => {
      if (gameState !== "playing" || currentPlayerConfig.isAI) return;
      const pos = getMousePos(e);
      if (phase === "place") {
        const clamped = clampToSector(pos.x, pos.y);
        setPlacementPos(clamped);
      } else if (phase === "aim" && aim && placementPos) {
        setAim((a) => (a ? { ...a, curr: pos } : a));
      }
    };

    const onDown = (e) => {
      if (gameState !== "playing") return;
      
      if (currentPlayerConfig.isAI) return;
      
      if (phase === "roundEnd") {
        setSeed((s) => (s + 1) >>> 0);
        return;
      }
      
      const pos = getMousePos(e);
      if (phase === "place") {
        const clamped = clampToSector(pos.x, pos.y);
        setPlacementPos(clamped);
        setPhase("aim");
        return;
      }
      if (phase === "aim") {
        if (!placementPos) return;
        setAim({ start: { ...placementPos }, curr: pos });
      }
    };

    const onUp = (e) => {
      if (gameState !== "playing" || currentPlayerConfig.isAI) return;
      if (phase !== "aim" || !aim || !placementPos) return;
      
      const pos = getMousePos(e);
      const dx = pos.x - placementPos.x;
      const dy = pos.y - placementPos.y;
      const MAX_DRAG = 200, scale = 0.06;
      const mag = Math.min(MAX_DRAG, Math.hypot(dx, dy));
      const ang = Math.atan2(dy, dx);
      const vx = -(Math.cos(ang) * mag) * scale;
      const vy = -(Math.sin(ang) * mag) * scale;
      
      const x = placementPos.x, y = placementPos.y;
      const newPuck = {
        x, y, vx, vy, r: PUCK_R,
        owner: currentPlayer,
        color: playerColor(currentPlayer)
      };
      setPucks((prev) => [...prev, newPuck]);
      setAim(null);
      setPlacementPos(null);
      setPhase("rolling");
      setTimerActive(false);
    };

    window.addEventListener("mousemove", onMove);
    canvas.addEventListener("mousedown", onDown);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      canvas.removeEventListener("mousedown", onDown);
      window.removeEventListener("mouseup", onUp);
    };
  }, [gameState, phase, placementPos, currentPlayer, aim, currentPlayerConfig.isAI]);

  // UI
  return (
    <PageBG>
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px',
        padding: '24px',
        minHeight: '100vh',
        color: 'white'
      }}>
        <div style={{
          textAlign: 'center',
          width: '100%',
          maxWidth: '1200px'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 800,
            background: 'linear-gradient(45deg, #60a5fa, #a855f7)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            margin: '0 0 1rem 0',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            Frozzlers {isGuestMode ? '– Guest Mode' : '– Local Game'}
          </h1>
        </div>

        {gameState === "lobby" && !isGuestMode ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            padding: '32px',
            background: 'rgba(15, 23, 42, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRadius: '16px',
            border: '2px solid rgba(96, 165, 250, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '18px', fontWeight: '500', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
                Players:
                <select 
                  style={{
                    marginLeft: '8px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: 'white',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  value={numPlayers} 
                  onChange={(e) => setNumPlayers(Number(e.target.value))}
                >
                  {[2, 3, 4].map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </label>
              <label style={{ fontSize: '18px', fontWeight: '500', color: 'white', display: 'flex', alignItems: 'center', gap: '12px' }}>
                Pucks each:
                <select 
                  style={{
                    marginLeft: '8px',
                    backgroundColor: '#374151',
                    border: '1px solid #4b5563',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    color: 'white',
                    transition: 'all 0.2s',
                    outline: 'none'
                  }}
                  value={pucksPerPlayer} 
                  onChange={(e) => setPucksPerPlayer(Number(e.target.value))}
                >
                  {[3, 5, 7].map((n) => (<option key={n} value={n}>{n}</option>))}
                </select>
              </label>
            </div>
            <ModernButton variant="primary" onClick={startGame}>
              Start Game
            </ModernButton>
          </div>
        ) : gameState === "playing" || isGuestMode ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <ModernButton onClick={() => setSeed((s) => (s + 1) >>> 0)}>
              New Layout
            </ModernButton>
            <ModernButton 
              onClick={() => { setSeed(seed); setPucks([]); setTurn(0); setPhase("place"); setSectorIdx([0,0,0,0]); setPlacementPos(null); setTimeLeft(MOVE_TIME_LIMIT); }} 
              title="Reset current layout"
            >
              Reset Round
            </ModernButton>
            <ModernButton 
              variant="danger"
              onClick={() => {
                if (isGuestMode) {
                  window.history.back();
                } else {
                  setGameState("lobby");
                }
              }}
            >
              {isGuestMode ? 'Back to Menu' : 'Exit to Lobby'}
            </ModernButton>
          </div>
        ) : null}

        <canvas 
          ref={canvasRef} 
          width={W} 
          height={H} 
          style={{
            borderRadius: '16px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid rgba(75, 85, 99, 0.5)',
            backgroundColor: 'transparent' // KEY: Canvas must be transparent!
          }} 
        />

        <div style={{ color: 'white', maxWidth: '800px', textAlign: 'center' }}>
          {gameState === "lobby" && !isGuestMode ? (
            <p style={{ fontSize: '18px', color: '#d1d5db', margin: 0 }}>
              Choose the number of players and pucks, then press <span style={{ color: '#60a5fa', fontWeight: '600' }}>Start Game</span>. 
              You can return here anytime with "Exit to Lobby".
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <p style={{ fontSize: '18px', color: '#d1d5db', margin: 0 }}>
                <span style={{ color: '#60a5fa', fontWeight: '600' }}>Turn flow:</span> {currentPlayerConfig.isAI ? 'AI is thinking...' : 'Move your puck anywhere inside the glowing sector (it follows your mouse). Click to place it. Then click and drag to charge power; release to shoot.'}
              </p>
              <p style={{ color: '#9ca3af', margin: 0 }}>
                <span style={{ color: '#a855f7', fontWeight: '600' }}>Scoring:</span> Center dot = 15, center ring = 10, next rings = 5 / 3 / 1. Pucks outside rings score 0.
              </p>
              {isGuestMode && playerConfigs.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '8px' }}>
                  {playerConfigs.map((config, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ 
                        width: '16px', 
                        height: '16px', 
                        borderRadius: '50%', 
                        backgroundColor: config.color,
                        border: currentPlayer === idx ? '2px solid white' : '1px solid #374151'
                      }}></div>
                      <span style={{ 
                        color: currentPlayer === idx ? '#ffffff' : '#9ca3af',
                        fontWeight: currentPlayer === idx ? 'bold' : 'normal'
                      }}>
                        {config.name} {config.isAI ? `(${config.aiDifficulty})` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PageBG>
  );
}
